import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lightbulb, BookOpenCheck, Brain, Wallet as WalletIcon, Power, Sparkles } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { formatPhone } from "../utils/phone";
import LanguageSelector from "../components/LanguageSelector";
import AnimatedFeatureIcon from "../components/AnimatedFeatureIcon";

const FEATURE_BASE = [
  { type: "hint",         bg: "bg-yellow-300",  icon: Lightbulb,     titleKey: "math_hint",     descKey: "math_hint_desc",     priceKey: "hint_price",         defaultCost: 4, route: "/math/capture/hint" },
  { type: "solution",     bg: "bg-emerald-300", icon: BookOpenCheck, titleKey: "math_solution", descKey: "math_solution_desc", priceKey: "solution_price",     defaultCost: 5, route: "/math/capture/solution" },
  { type: "mental-math",  bg: "bg-sky-300",     icon: Brain,         titleKey: "math_mental",   descKey: "math_mental_desc",   priceKey: "mental_math_price", defaultCost: 3, route: "/math/mental-math" },
];

export default function MathPage() {
  const navigate = useNavigate();
  const { user, lang, config, logout } = useAuth();
  const balance = user?.wallet_balance ?? 0;
  const currency = config?.currency_symbol || "₹";
  const features = FEATURE_BASE.map((f) => ({ ...f, cost: config?.[f.priceKey] ?? f.defaultCost }));

  return (
    <div className="page-shell" data-testid="math-page">
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          aria-label="back to home"
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <button
          data-testid="wallet-pill"
          onClick={() => navigate("/wallet")}
          className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-500 text-white px-4 py-2 rounded-full font-bold shadow-[0_4px_0_rgba(0,0,0,0.1)] border-2 border-emerald-600/20 active:translate-y-1 active:shadow-none transition-all"
        >
          <WalletIcon size={18} strokeWidth={2.5} />
          {currency}<span data-testid="wallet-balance">{balance}</span>
        </button>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
            aria-label="logout"
          >
            <Power size={18} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={16} strokeWidth={2.5} className="text-violet-500" /> {t(lang, "welcome")}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "module_math_title")}
        </h1>
        <p className="text-base sm:text-lg text-cyan-50/95 mt-1 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">
          {t(lang, "math_tagline")}
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-5">
        {features.map((f, i) => {
          return (
            <motion.button
              key={f.type}
              data-testid={`feature-math-${f.type}-btn`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(f.route)}
              className={`relative w-full ${f.bg} rounded-3xl p-5 text-left card-squish border-4 border-white flex items-center gap-4`}
            >
              <div className="w-16 h-16 rounded-2xl bg-white/90 flex items-center justify-center shadow-inner">
                <AnimatedFeatureIcon type={f.type} icon={f.icon} size={32} className="text-slate-800" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-2xl font-bold text-slate-800 truncate">
                  {t(lang, f.titleKey)}
                </h3>
                <p className="text-sm text-slate-700 font-semibold mt-0.5 truncate">
                  {t(lang, f.descKey)}
                </p>
              </div>
              <div className={`shrink-0 px-3 py-1.5 rounded-full font-heading font-bold text-base ${
                f.cost === 0 ? "bg-emerald-500 text-white" : "bg-white text-slate-800 border-2 border-slate-900/10"
              }`}>
                {f.cost === 0 ? t(lang, "free_label") : `${currency}${f.cost}`}
              </div>
            </motion.button>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-cyan-100/80 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
        {formatPhone(user?.phone)}
      </p>
    </div>
  );
}
